import React, { useState, useEffect } from 'react';
import {
  LogOut,
  BarChart3,
  TrendingUp,
  Users,
  Building,
  Target,
  Settings
} from 'lucide-react';
import AdminAuthGuard from './components/AdminAuthGuard';
import ActivityFeed from './components/ActivityFeed';
import UnifiedSearch from './components/UnifiedSearch';
import EntityDetail from './components/EntityDetail';
import LeadSourceAnalytics from './components/LeadSourceAnalytics';
import SessionDetailModal from './components/SessionDetailModal';
import AdminSettings from './components/AdminSettings';
import { fetchDashboardMetrics, fetchOrganizations, fetchPersons, fetchSessionBySessionId } from './services/adminService';
import { AdminUser, AdminOrganization, AdminSession } from './types/admin.types';

interface DashboardMetrics {
  totalSessions: number;
  totalUsers: number;
  totalOrganizations: number;
  averageTAS: number;
  sessionsThisMonth: number;
  sessionsLastMonth: number;
}

const AdminDashboard: React.FC = () => {
  const [selectedEntity, setSelectedEntity] = useState<AdminUser | AdminOrganization | null>(null);
  const [entityType, setEntityType] = useState<'person' | 'organization' | null>(null);
  const [selectedSession, setSelectedSession] = useState<AdminSession | null>(null);
  const [showLeadSources, setShowLeadSources] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalSessions: 0,
    totalUsers: 0,
    totalOrganizations: 0,
    averageTAS: 0,
    sessionsThisMonth: 0,
    sessionsLastMonth: 0
  });

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const data = await fetchDashboardMetrics();
      setMetrics(data);
    } catch (error) {
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth_token');
    window.location.reload();
  };

  const handleSelectPerson = async (user: AdminUser | { email: string; name?: string }) => {
    // If we receive a full user object, use it directly
    if ('userId' in user && 'totalSessions' in user) {
      setSelectedEntity(user);
      setEntityType('person');
      return;
    }
    
    // Otherwise, fetch the full user data by email
    const userEmail = typeof user === 'string' ? user : user.email;
    try {
      const users = await fetchPersons();
      const fullUser = users.find(u => u.email === userEmail);
      
      if (fullUser) {
        setSelectedEntity(fullUser);
        setEntityType('person');
      } else {
        // Fallback if user not found
        const fallbackUser = {
          ...user,
          userId: userEmail,
          totalSessions: 0,
          firstSeen: new Date(),
          lastSeen: new Date(),
          lastActive: new Date(),
          organizationName: '',
          organizationsAssessed: [],
          isRooster: false
        } as unknown as AdminUser;
        setSelectedEntity(fallbackUser);
        setEntityType('person');
      }
    } catch (error) {
    }
  };

  const handleSelectOrganization = async (org: AdminOrganization | { name: string }) => {
    // If we receive a full organization object, use it directly
    if ('totalAssessments' in org) {
      setSelectedEntity(org);
      setEntityType('organization');
      return;
    }
    
    // Otherwise, fetch the full organization data by name
    const orgName = typeof org === 'string' ? org : org.name;
    try {
      const organizations = await fetchOrganizations();
      const fullOrg = organizations.find(o => o.name === orgName);
      
      if (fullOrg) {
        setSelectedEntity(fullOrg);
        setEntityType('organization');
      } else {
        // Fallback if organization not found
        const fallbackOrg = {
          name: orgName,
          totalAssessments: 0,
          assessors: [],
          firstAssessment: new Date(),
          lastAssessment: new Date()
        } as unknown as AdminOrganization;
        setSelectedEntity(fallbackOrg);
        setEntityType('organization');
      }
    } catch (error) {
    }
  };

  const handleSelectSession = (session: AdminSession) => {
    // Open session detail modal
    setSelectedSession(session);
  };

  const handleCloseSessionDetail = () => {
    setSelectedSession(null);
  };

  const handleSelectSessionBySessionId = async (sessionId: string) => {
    try {
      const session = await fetchSessionBySessionId(sessionId);
      if (session) {
        setSelectedSession(session);
      } else {
        console.error('Session not found:', sessionId);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  const handleCloseDetail = () => {
    setSelectedEntity(null);
    setEntityType(null);
  };

  const handleDeleteSuccess = () => {
    // Refresh the activity feed and metrics
    setRefreshKey(prev => prev + 1);
    loadMetrics();
  };

  // Calculate month-over-month growth
  const monthGrowth = metrics.sessionsLastMonth > 0 
    ? ((metrics.sessionsThisMonth - metrics.sessionsLastMonth) / metrics.sessionsLastMonth * 100).toFixed(1)
    : '0';

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  TAS Management Console
                </h1>
                <span className="bg-sky-100 text-sky-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  Admin
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    setShowLeadSources(!showLeadSources);
                    setShowSettings(false);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                    ${showLeadSources
                      ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  <Target className="h-4 w-4" />
                  Lead Sources
                </button>
                <button
                  onClick={() => {
                    setShowSettings(!showSettings);
                    setShowLeadSources(false);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors
                    ${showSettings
                      ? 'bg-sky-100 text-sky-700 hover:bg-sky-200'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
                >
                  <Settings className="h-4 w-4" />
                  Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Conditionally show Settings, Lead Source Analytics, or regular dashboard */}
            {showSettings ? (
              <AdminSettings />
            ) : showLeadSources ? (
              <LeadSourceAnalytics />
            ) : (
              <>
                {/* Search Bar */}
                <div className="mb-6">
                  <UnifiedSearch
                    onSelectPerson={(user) => void handleSelectPerson(user)}
                    onSelectOrganization={(org) => void handleSelectOrganization(org)}
                  />
                </div>

                {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Sessions</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {metrics.totalSessions.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {metrics.sessionsThisMonth} this month
                    </p>
                  </div>
                  <div className="p-3 bg-sky-50 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-sky-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Organizations</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {metrics.totalOrganizations.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Active accounts
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <Building className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Assessors</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {metrics.totalUsers.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Unique users
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Avg TAS Score</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {Math.round(metrics.averageTAS)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {monthGrowth}% from last month
                    </p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Feed */}
            <ActivityFeed
              key={refreshKey}
              onSelectPerson={(email, name) => void handleSelectPerson({ email, name })}
              onSelectOrganization={(name) => void handleSelectOrganization({ name })}
              onSelectSession={handleSelectSession}
            />
              </>
            )}
          </div>
        </main>

        {/* Entity Detail Panel */}
        <EntityDetail
          entity={selectedEntity}
          entityType={entityType}
          onClose={handleCloseDetail}
          onSelectPerson={(email, name) => void handleSelectPerson({ email, name })}
          onSelectOrganization={(name) => void handleSelectOrganization({ name })}
          onSelectSession={handleSelectSession}
        />

        {/* Session Detail Modal */}
        <SessionDetailModal
          session={selectedSession}
          onClose={handleCloseSessionDetail}
          onSelectSession={handleSelectSessionBySessionId}
          onDeleteSuccess={handleDeleteSuccess}
        />
      </div>
    </AdminAuthGuard>
  );
};

export default AdminDashboard;